import { useEffect, useCallback } from 'react';
import type { Lead } from '../../types';
import type { CellCoord, TableColumn } from '../../types/table';
import { KEYBOARD_NAV } from '../../constants/table';

export interface UseKeyboardNavigationProps {
  anchor: CellCoord | null;
  leads: Lead[];
  columns: TableColumn[];
  isDragging: boolean;
  onMove: (direction: 'up' | 'down' | 'left' | 'right', extendSelection: boolean) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onSelectCell: (rowId: string, colId: string, isMulti?: boolean) => void;
  onSelectRange: (from: CellCoord, to: CellCoord) => void;
}

/**
 * Hook for handling keyboard navigation in the table
 * Supports arrow keys, Home/End, PageUp/PageDown, Ctrl+A, and Escape
 */
export const useKeyboardNavigation = ({
  anchor,
  leads,
  columns,
  // isDragging - reserved for future use (drag detection during keyboard nav)
  onMove,
  onSelectAll,
  onClearSelection,
  onSelectCell,
  onSelectRange,
}: UseKeyboardNavigationProps): void => {
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Don't interfere if user is typing in an input
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      return;
    }

    const isShift = e.shiftKey;
    const isCtrlOrCmd = e.ctrlKey || e.metaKey;

    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        onMove('up', isShift);
        break;

      case 'ArrowDown':
        e.preventDefault();
        onMove('down', isShift);
        break;

      case 'ArrowLeft':
        e.preventDefault();
        onMove('left', isShift);
        break;

      case 'ArrowRight':
        e.preventDefault();
        onMove('right', isShift);
        break;

      case 'a':
      case 'A':
        if (isCtrlOrCmd) {
          e.preventDefault();
          onSelectAll();
        }
        break;

      case 'Escape':
        e.preventDefault();
        onClearSelection();
        // Re-select first cell after clearing
        if (leads.length > 0 && columns.length > 0 && leads[0].id) {
          onSelectCell(leads[0].id, columns[0].key, false);
        }
        break;

      case 'Home':
        if (anchor) {
          e.preventDefault();
          const firstCol = columns[0];
          if (firstCol.key) {
            if (isShift) {
              onSelectRange(anchor, { rowId: anchor.rowId, colId: firstCol.key });
            } else {
              onSelectCell(anchor.rowId, firstCol.key, false);
            }
          }
        }
        break;

      case 'End':
        if (anchor) {
          e.preventDefault();
          const lastCol = columns[columns.length - 1];
          if (lastCol.key) {
            if (isShift) {
              onSelectRange(anchor, { rowId: anchor.rowId, colId: lastCol.key });
            } else {
              onSelectCell(anchor.rowId, lastCol.key, false);
            }
          }
        }
        break;

      case 'PageUp':
        if (anchor) {
          e.preventDefault();
          const currentRowIdx = leads.findIndex(l => l.id === anchor.rowId);
          const newRowIdx = Math.max(0, currentRowIdx - KEYBOARD_NAV.PAGE_JUMP_ROWS);
          const newLead = leads[newRowIdx];
          if (newLead.id) {
            if (isShift) {
              onSelectRange(anchor, { rowId: newLead.id, colId: anchor.colId });
            } else {
              onSelectCell(newLead.id, anchor.colId, false);
            }
          }
        }
        break;

      case 'PageDown':
        if (anchor) {
          e.preventDefault();
          const currentRowIdx = leads.findIndex(l => l.id === anchor.rowId);
          const newRowIdx = Math.min(leads.length - 1, currentRowIdx + KEYBOARD_NAV.PAGE_JUMP_ROWS);
          const newLead = leads[newRowIdx];
          if (newLead.id) {
            if (isShift) {
              onSelectRange(anchor, { rowId: newLead.id, colId: anchor.colId });
            } else {
              onSelectCell(newLead.id, anchor.colId, false);
            }
          }
        }
        break;
    }
  }, [anchor, leads, columns, onMove, onSelectAll, onClearSelection, onSelectCell, onSelectRange]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
};

/**
 * Helper hook to create move handler for keyboard navigation
 */
export const useMoveActiveCell = (
  anchor: CellCoord | null,
  leads: Lead[],
  columns: TableColumn[],
  onSelectCell: (rowId: string, colId: string, isMulti?: boolean) => void,
  onSelectRange: (from: CellCoord, to: CellCoord) => void,
) => {
  return useCallback((direction: 'up' | 'down' | 'left' | 'right', extendSelection: boolean = false) => {
    if (!anchor) return;

    const currentRowIdx = leads.findIndex(l => l.id === anchor.rowId);
    const currentColIdx = columns.findIndex(c => c.key === anchor.colId);

    if (currentRowIdx === -1 || currentColIdx === -1) return;

    let newRowIdx = currentRowIdx;
    let newColIdx = currentColIdx;

    switch (direction) {
      case 'up':
        newRowIdx = Math.max(0, currentRowIdx - 1);
        break;
      case 'down':
        newRowIdx = Math.min(leads.length - 1, currentRowIdx + 1);
        break;
      case 'left':
        newColIdx = Math.max(0, currentColIdx - 1);
        break;
      case 'right':
        newColIdx = Math.min(columns.length - 1, currentColIdx + 1);
        break;
    }

    const newLead = leads[newRowIdx];
    const newCol = columns[newColIdx];

    if (!newLead.id || !newCol.key) return;

    if (extendSelection) {
      // Shift+Arrow: Extend selection from original anchor
      onSelectRange(anchor, { rowId: newLead.id, colId: newCol.key });
    } else {
      // Arrow: Move to new cell, collapse selection
      onSelectCell(newLead.id, newCol.key, false);
    }

    return { rowId: newLead.id, colId: newCol.key };
  }, [anchor, leads, columns, onSelectCell, onSelectRange]);
};
